import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, FileText, Package, MessageSquare, Users, Megaphone, Bell, UserCog, LogOut, Menu, Wrench,
  Star, Video, HelpCircle, Settings, Globe, Inbox, ClipboardList,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Logo } from "../components/site/Brand";
import { ThemeToggle } from "../components/site/ThemeToggle";
import { useAuth } from "../context/AuthContext";
import { RealtimeProvider, useRealtimeEvent } from "../context/RealtimeContext";
import { api, fmtDate } from "../lib/api";

const NAV = {
  customer: [["", "Overview", LayoutDashboard], ["quotations", "My Quotations", FileText], ["orders", "My Orders", Package],
    ["messages", "Messages", MessageSquare], ["groups", "Group Chats", Users], ["announcements", "Announcements & Promos", Megaphone],
    ["notifications", "Notifications", Bell], ["profile", "Profile Settings", UserCog]],
  grinder: [["", "Overview", LayoutDashboard], ["orders", "Assigned Orders", ClipboardList], ["progress", "Order Progress", Package],
    ["messages", "Messages", MessageSquare], ["groups", "Group Chats", Users], ["announcements", "Announcements", Megaphone],
    ["notifications", "Notifications", Bell], ["profile", "Profile Settings", UserCog]],
  admin: [["", "Overview", LayoutDashboard], ["customers", "Customers", Users], ["grinders", "Grinders", Wrench],
    ["quotations", "Quotations", FileText], ["orders", "Orders", Package], ["messages", "Messages", MessageSquare],
    ["groups", "Group Chats", Users], ["inquiries", "Inquiries", Inbox], ["announcements", "Announcements & Promos", Megaphone],
    ["notifications", "Notifications", Bell], ["content", "Website Content", Globe], ["reviews", "Reviews", Star],
    ["videos", "Video Tutorials", Video], ["faqs", "FAQs", HelpCircle], ["settings", "Settings", Settings], ["profile", "Profile", UserCog]],
};

function Sidebar({ role, counts, onNav }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const badge = (k) => (k === "messages" ? counts.direct : k === "groups" ? counts.group : k === "notifications" ? counts.notifs : 0);
  return (
    <div className="h-full flex flex-col">
      <Link to="/" className="px-5 h-16 flex items-center border-b border-[#007bff]/20" data-testid="dash-logo"><Logo className="text-2xl" /></Link>
      <nav className="flex-1 overflow-y-auto scroll-thin p-3 space-y-1" onClick={onNav}>
        {NAV[role].map(([to, l, I]) => (
          <NavLink key={to} to={`/dashboard${to ? `/${to}` : ""}`} end
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors ${isActive ? "bg-[#007bff]/20 text-white border border-[#007bff]/40" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"}`}
            data-testid={`dash-nav-${to || "overview"}`}>
            <I className="w-[18px] h-[18px]" /><span className="flex-1">{l}</span>
            {badge(to) > 0 && <span className="min-w-5 h-5 px-1.5 rounded-full bg-[#00d9ff] text-[#021] text-xs font-bold grid place-items-center">{badge(to)}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[#007bff]/20">
        <div className="px-3 py-2 text-sm"><p className="text-white font-semibold truncate">{user.name}</p><p className="text-xs text-[#00d9ff] uppercase tracking-widest">{user.role}</p></div>
        <button onClick={async () => { await logout(); nav("/login"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5" data-testid="dash-logout-btn"><LogOut className="w-4 h-4" /> Log out</button>
      </div>
    </div>
  );
}

function Bell_({ data, reload }) {
  const nav = useNavigate();
  const open = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    reload();
    if (n.link) nav(n.link);
  };
  return (
    <Popover>
      <PopoverTrigger className="relative p-2 rounded-lg hover:bg-white/5" data-testid="notification-bell">
        <Bell className="w-5 h-5 text-slate-200" />
        {data.unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-[#00d9ff] text-[#021] text-[11px] font-bold grid place-items-center" data-testid="notification-unread-count">{data.unread}</span>}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[92vw] max-w-sm p-0 bg-[#060d20] border-[#007bff]/40">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#007bff]/20">
          <p className="font-cond font-bold uppercase tracking-wider">Notifications</p>
          <button className="text-xs text-[#00d9ff]" onClick={async () => { await api.post("/notifications/read-all"); reload(); }} data-testid="notifications-mark-all">Mark all read</button>
        </div>
        <div className="max-h-96 overflow-y-auto scroll-thin">
          {data.items.slice(0, 15).map((n) => (
            <button key={n.id} onClick={() => open(n)} className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 ${n.read ? "opacity-60" : ""}`} data-testid={`notification-item-${n.id}`}>
              <p className="text-sm font-semibold text-white flex items-center gap-2">{!n.read && <span className="w-2 h-2 rounded-full bg-[#00d9ff]" />}{n.title}</p>
              {n.body && <p className="text-xs text-slate-400 line-clamp-2">{n.body}</p>}
              <p className="text-[11px] text-slate-500 mt-0.5">{fmtDate(n.created_at)}</p>
            </button>
          ))}
          {data.items.length === 0 && <p className="p-6 text-center text-sm text-slate-500">No notifications yet</p>}
        </div>
        <Link to="/dashboard/notifications" className="block text-center py-2.5 text-sm text-[#00d9ff] border-t border-[#007bff]/20" data-testid="notifications-view-all">View all</Link>
      </PopoverContent>
    </Popover>
  );
}

function Shell() {
  const { user } = useAuth();
  const [mobile, setMobile] = useState(false);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [unread, setUnread] = useState({ direct: 0, group: 0 });
  const [banner, setBanner] = useState(null);

  const reload = useCallback(async () => {
    const [n, u] = await Promise.all([api.get("/notifications"), api.get("/messages/unread-count")]);
    setNotifs(n.data);
    setUnread(u.data);
  }, []);
  useEffect(() => {
    reload();
    const t = setInterval(reload, 20000);
    if (user.role !== "admin") api.get("/announcements").then((r) => setBanner(r.data.find((a) => a.important && a.published) || null)).catch(() => {});
    return () => clearInterval(t);
  }, [reload, user.role]);
  useRealtimeEvent((m) => {
    if (m.event === "notification") {
      toast(m.data.title, { description: m.data.body });
      reload();
    }
    if (m.event === "message" || m.event === "read") reload();
  });

  const counts = { ...unread, notifs: notifs.unread };
  return (
    <div className="min-h-screen bg-[#04060e] lg:pl-72" data-testid="dashboard-shell">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-72 bg-[#050a18] border-r border-[#007bff]/20"><Sidebar role={user.role} counts={counts} /></aside>
      <Sheet open={mobile} onOpenChange={setMobile}>
        <SheetContent side="left" className="p-0 w-[85vw] max-w-xs bg-[#050a18] border-[#007bff]/30"><SheetTitle className="sr-only">Navigation</SheetTitle><Sidebar role={user.role} counts={counts} onNav={() => setMobile(false)} /></SheetContent>
      </Sheet>
      <header className="sticky top-0 z-30 h-16 bg-[#04060e]/85 backdrop-blur-xl border-b border-[#007bff]/20 flex items-center justify-between px-4 sm:px-6">
        <button className="lg:hidden p-2" onClick={() => setMobile(true)} data-testid="dash-mobile-menu-btn"><Menu className="w-6 h-6" /></button>
        <p className="hidden lg:block font-cond uppercase tracking-[0.3em] text-sm text-slate-500">{user.role} dashboard</p>
        <div className="flex items-center gap-2">
          <Link to="/" className="text-sm text-slate-400 hover:text-white px-2" data-testid="dash-view-site">View site</Link>
          <ThemeToggle />
          <Bell_ data={notifs} reload={reload} />
        </div>
      </header>
      {banner && (
        <div className="mx-4 sm:mx-6 mt-4 rounded-xl border border-[#00d9ff]/40 bg-gradient-to-r from-[#0a2a6b] to-[#04112b] px-4 py-3 flex items-start gap-3" data-testid="announcement-banner">
          <Megaphone className="w-5 h-5 text-[#00d9ff] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0"><p className="font-semibold text-white">{banner.title}</p><p className="text-sm text-slate-300 line-clamp-2">{banner.description}</p></div>
          <button className="text-slate-400 text-sm" onClick={() => setBanner(null)} data-testid="announcement-banner-close">✕</button>
        </div>
      )}
      <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px]"><Outlet context={{ reloadCounts: reload }} /></main>
    </div>
  );
}

export default function DashboardLayout() {
  const { user } = useAuth();
  if (user === null) return <div className="h-screen grid place-items-center text-[#00d9ff]">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <RealtimeProvider><Shell /></RealtimeProvider>;
}

export function RoleGate({ roles, children }) {
  const { user } = useAuth();
  if (!roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}
